import * as React from 'react';
import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import InputHandler from 'Components/InputHandler';
import type { IFormContext } from 'Containers/FormContextHandler';
import { withFormContext } from 'Containers/FormContextHandler';
import { FormattedMessage } from 'react-intl';
import colors from '../../../../../../config/theme/colors';

const SectionName = ({ title }: { title: string }) => {
  return (
    <div style={{ color: colors.darkGray, marginBottom: '1rem' }}>
      <FormattedMessage id={title} />
    </div>
  );
};

const LocationDetailsInput = ({
  location,
  onChangeLocationDetail,
  getFieldFromSymptomLocation,
}: {
  location: TPolyneuroSymptomLocation;
  onChangeLocationDetail: (
    field: 'locationDetail' | 'locationDetail2',
    values: TOnChangeValues,
    location: TPolyneuroSymptomLocation,
  ) => void;
  getFieldFromSymptomLocation: (
    location: string,
    field: 'locationDetail' | 'locationDetail2',
  ) => string[] | string | undefined | null;
}) => {
  const locationDetailOpts = ['trunk', 'face'].includes(location)
    ? ['symmetrical', 'asymmetrical']
    : ['distal', 'proximal', 'entireLimb'];

  const showDetails2 = ['asymmetrical'].includes(getFieldFromSymptomLocation(location, 'locationDetail') as string);

  return (
    <div style={{ marginBottom: '2rem' }}>
      <SectionName title={'diagnosis.polyneuro.symptoms.locationDetail.title'} />
      <InputHandler
        type="Radio"
        editing={true}
        name={'locationDetail'}
        formData={{
          document: { locationDetail: getFieldFromSymptomLocation(location, 'locationDetail') },
          onChange: values => onChangeLocationDetail('locationDetail', values, location),
        }}
        options={locationDetailOpts}
        optionFormatter={o => <FormattedMessage id={'diagnosis.polyneuro.symptoms.locationDetail.' + o} />}
      />
      {showDetails2 && (
        <>
          <SectionName title={'diagnosis.polyneuro.symptoms.locationDetail2.title'} />
          <InputHandler
            type="Radio"
            editing={true}
            name={'locationDetail2'}
            formData={{
              document: { locationDetail2: getFieldFromSymptomLocation(location, 'locationDetail2') },
              onChange: values => onChangeLocationDetail('locationDetail2', values, location),
            }}
            options={['right', 'left']}
            optionFormatter={o => <FormattedMessage id={'diagnosis.polyneuro.symptoms.locationDetail2.' + o} />}
          />
        </>
      )}
    </div>
  );
};

const LocationInputNumbnessetc = ({
  name,
  onChangeSymptomLocation,
  getSymptomLocationData,
}: {
  name: TPolyneuroSymptoms;
  onChangeSymptomLocation: (
    name: TPolyneuroSymptoms,
    field: string,
    values: TOnChangeValues,
    location?: TPolyneuroSymptomLocation,
  ) => void;
  getSymptomLocationData: (name: TPolyneuroSymptoms) => IPolyneuroSymptomLocation | undefined;
}) => {
  const getFieldFromSymptomLocation = (location: string, field: 'location' | 'locationDetail' | 'locationDetail2') => {
    const symptomLocation = getSymptomLocationData(name);
    if (field === 'location') return symptomLocation?.locations?.map(locs => locs.location);
    if (field === 'locationDetail')
      return symptomLocation?.locations?.find(locs => locs.location === location)?.locationDetail;
    else return symptomLocation?.locations?.find(locs => locs.location === location)?.locationDetail2;
  };

  const onChangeLocationDetail = (
    field: 'locationDetail' | 'locationDetail2',
    values: TOnChangeValues,
    location: TPolyneuroSymptomLocation,
  ) => onChangeSymptomLocation(name, field, values, location);

  return (
    <div style={{ marginBottom: '2rem' }}>
      <SectionName title={'diagnosis.polyneuro.symptoms.location.title'} />
      <InputHandler
        type="Checkbox"
        editing={true}
        name={'location'}
        formData={{
          document: { location: getFieldFromSymptomLocation('', 'location') },
          onChange: values => onChangeSymptomLocation(name, 'location', values),
        }}
        options={['leftLowerLimb', 'rightLowerLimb', 'leftUpperLimb', 'rightUpperLimb', 'trunk', 'face']}
        optionFormatter={o => <FormattedMessage id={'diagnosis.polyneuro.symptoms.location.' + o} />}
        checkboxCentered={false}
        optionSpecificElements={[
          {
            leftLowerLimb: (
              <LocationDetailsInput
                location="leftLowerLimb"
                onChangeLocationDetail={onChangeLocationDetail}
                getFieldFromSymptomLocation={getFieldFromSymptomLocation}
              />
            ),
            rightLowerLimb: (
              <LocationDetailsInput
                location="rightLowerLimb"
                onChangeLocationDetail={onChangeLocationDetail}
                getFieldFromSymptomLocation={getFieldFromSymptomLocation}
              />
            ),
            leftUpperLimb: (
              <LocationDetailsInput
                location="leftUpperLimb"
                onChangeLocationDetail={onChangeLocationDetail}
                getFieldFromSymptomLocation={getFieldFromSymptomLocation}
              />
            ),
            rightUpperLimb: (
              <LocationDetailsInput
                location="rightUpperLimb"
                onChangeLocationDetail={onChangeLocationDetail}
                getFieldFromSymptomLocation={getFieldFromSymptomLocation}
              />
            ),
            trunk: (
              <LocationDetailsInput
                location="trunk"
                onChangeLocationDetail={onChangeLocationDetail}
                getFieldFromSymptomLocation={getFieldFromSymptomLocation}
              />
            ),
            face: (
              <LocationDetailsInput
                location="face"
                onChangeLocationDetail={onChangeLocationDetail}
                getFieldFromSymptomLocation={getFieldFromSymptomLocation}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

const PolyneuroSymptomsForm = ({ editing, formData }: IFormContext<IPolyneuroSymptoms>): React.JSX.Element => {
  const onChangeSymptomLocation = (
    name: TPolyneuroSymptoms,
    field: string,
    values: TOnChangeValues,
    /** Need this to change location details */
    location?: TPolyneuroSymptomLocation,
  ) => {
    const symptomLocations = [...(formData.document.symptomLocations || [])];
    const thisLocation = symptomLocations?.find(loc => loc.symptom === name);

    if (field === 'location') {
      const value = values[field] as Array<TPolyneuroSymptomLocation>;

      if (thisLocation) {
        if (field === 'location') {
          // Remove deleted locations
          let newLocations = (thisLocation.locations || []).filter(tl => value.includes(tl.location));
          const missingLocations = value
            .filter(val => !newLocations.map(nl => nl.location).includes(val))
            .map(mis => ({ location: mis }));

          newLocations = [...newLocations, ...missingLocations];
          formData.onChange?.({
            symptomLocations: symptomLocations.map(sl =>
              sl.symptom === name ? { symptom: name, locations: newLocations } : sl,
            ),
          });
        }
      } else {
        const missingLocations = value.map(mis => ({ location: mis }));
        formData.onChange?.({
          symptomLocations: [...symptomLocations, { symptom: name, locations: missingLocations }],
        });
      }
    }

    if ((field === 'locationDetail' || field === 'locationDetail2') && thisLocation) {
      const value = values[field] as TPolyneuroSymptomLimbLocationDetail | TPolyneuroSymptomFaceTrunkLocationDetail;
      const newLocation = {
        ...thisLocation,
        locations: thisLocation.locations?.map(tl => (tl.location === location ? { ...tl, [field]: value } : tl)),
      };
      formData.onChange?.({ symptomLocations: symptomLocations.map(sl => (sl.symptom === name ? newLocation : sl)) });
    }
  };

  const getSymptomLocationData = (name: TPolyneuroSymptoms) => {
    const locations = formData.document.symptomLocations;
    return locations?.find(loc => loc.symptom === name);
  };

  return (
    <React.Fragment>
      <FormSection>
        <div style={{ marginBottom: '5rem' }} />
        <FormRow title="diagnosis.symptomOnsetDate">
          <InputHandler
            type="PartialDate"
            editing={!!editing}
            name="symptomOnsetDate"
            formData={formData}
            dateDefault="now"
          />
        </FormRow>
        <FormRow title="diagnosis.polyneuro.symptoms.timeCourse.title">
          <InputHandler
            type="Radio"
            editing={!!editing}
            name="timeCourse"
            formData={formData}
            options={['acute', 'chronic']}
            optionFormatter={o => <FormattedMessage id={'diagnosis.polyneuro.symptoms.timeCourse.' + o} />}
          />
        </FormRow>
        <FormRow title="diagnosis.polyneuro.symptoms.sensory">
          <InputHandler
            type="Checkbox"
            editing={!!editing}
            name="symptoms"
            formData={formData}
            options={['numbness', 'paresthesia', 'neuropathicPain', 'impairedBalance', 'dizziness']}
            optionFormatter={o => (
              <div style={{ marginBottom: '2rem' }}>
                <div>
                  <FormattedMessage id={'diagnosis.polyneuro.symptoms.symptom.' + o} />
                </div>
                <div style={{ fontSize: '1.4rem', fontWeight: 400 }}>
                  <FormattedMessage id={'diagnosis.polyneuro.symptoms.symptomDefinition.' + o} />
                </div>
              </div>
            )}
            checkboxCentered={false} //TODO: better prop
            optionSpecificElements={[
              {
                numbness: (
                  <LocationInputNumbnessetc
                    name="numbness"
                    onChangeSymptomLocation={onChangeSymptomLocation}
                    getSymptomLocationData={getSymptomLocationData}
                  />
                ),
                paresthesia: (
                  <LocationInputNumbnessetc
                    name="paresthesia"
                    onChangeSymptomLocation={onChangeSymptomLocation}
                    getSymptomLocationData={getSymptomLocationData}
                  />
                ),
                neuropathicPain: (
                  <LocationInputNumbnessetc
                    name="neuropathicPain"
                    onChangeSymptomLocation={onChangeSymptomLocation}
                    getSymptomLocationData={getSymptomLocationData}
                  />
                ),
              },
            ]}
          />
        </FormRow>
        <FormRow title="diagnosis.polyneuro.symptoms.motor">
          <InputHandler
            type="Checkbox"
            editing={!!editing}
            name="symptoms"
            formData={formData}
            options={['muscleWeakness', 'clumsiness', 'tremor']}
            optionFormatter={o => (
              <div style={{ marginBottom: '2rem' }}>
                <div>
                  <FormattedMessage id={'diagnosis.polyneuro.symptoms.symptom.' + o} />
                </div>
                <div style={{ fontSize: '1.4rem', fontWeight: 400 }}>
                  <FormattedMessage id={'diagnosis.polyneuro.symptoms.symptomDefinition.' + o} />
                </div>
              </div>
            )}
            checkboxCentered={false} //TODO: better prop
            optionSpecificElements={[
              {
                muscleWeakness: (
                  <LocationInputNumbnessetc
                    name="muscleWeakness"
                    onChangeSymptomLocation={onChangeSymptomLocation}
                    getSymptomLocationData={getSymptomLocationData}
                  />
                ),
                clumsiness: (
                  <LocationInputNumbnessetc
                    name="clumsiness"
                    onChangeSymptomLocation={onChangeSymptomLocation}
                    getSymptomLocationData={getSymptomLocationData}
                  />
                ),
                tremor: (
                  <LocationInputNumbnessetc
                    name="tremor"
                    onChangeSymptomLocation={onChangeSymptomLocation}
                    getSymptomLocationData={getSymptomLocationData}
                  />
                ),
              },
            ]}
          />
        </FormRow>
        <FormRow title="diagnosis.polyneuro.symptoms.autonomic">
          <InputHandler
            type="Checkbox"
            editing={!!editing}
            name="symptoms"
            formData={formData}
            options={['orthostaticHypotension', 'hyperhidrosis', 'hypohidrosis']}
            optionFormatter={o => (
              <div style={{ marginBottom: '2rem' }}>
                <div>
                  <FormattedMessage id={'diagnosis.polyneuro.symptoms.symptom.' + o} />
                </div>
                <div style={{ fontSize: '1.4rem', fontWeight: 400 }}>
                  <FormattedMessage id={'diagnosis.polyneuro.symptoms.symptomDefinition.' + o} />
                </div>
              </div>
            )}
          />
        </FormRow>
      </FormSection>
    </React.Fragment>
  );
};

export default withFormContext(PolyneuroSymptomsForm);
